import React, { useEffect, useState } from "react";
import { Sparkles, ArrowRight, X, Zap, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";

const DISMISS_KEY = "upgrade-banner-dismissed-at";

/* ── Converted to Tailwind utilities in Phase 5 (2026-08-24) ──────────────────
 * upgradeBanner.scss is deleted (TOKEN_CONTRACT §6.1). It held 20 colour literals
 * and no dark-mode block, because it did not need one: this is an ALWAYS-DARK panel,
 * the same shape as the /demo marketing hero, and §7 is what governs it.
 *
 * §8.1's file-level exemption does NOT apply. That needs a second theme axis — the
 * exam runner has one (the candidate's own dark toggle); a banner that is simply
 * dark in both themes does not. So every colour chains to a global token:
 *
 *   · The gradient was #1e3a8a → #312e81 → #4c1d95, i.e. dark navy deepening into
 *     violet. It is `--surface-dark` mixed progressively with `--primary`, so the
 *     upsell shown inside an institute's dashboard is finally in that institute's
 *     colour. At the default violet brand it lands within a few percent of the hexes
 *     it replaces. `color-mix` rather than `darken()`, which cannot take a `var()`.
 *   · The corner glow was rgba(139, 92, 246, 0.35) — the palette violet, spelled out.
 *     `hsl(var(--primary) / 0.35)`.
 *   · Every white was `--on-surface-dark`, which exists for exactly this.
 *   · ⚠️ The two status hues are LIGHTENED, deliberately. `--warning` (50 % L) and
 *     `--success` (42 % L) are chosen to be read on a light page; on this panel they
 *     are the "invisible on a dark hero" case §7 documents at /demo. Mixing toward
 *     white keeps them following the token instead of forking it. */
const PANEL_BG =
  "tw-bg-[linear-gradient(135deg,hsl(var(--surface-dark))_0%,color-mix(in_srgb,hsl(var(--primary))_28%,hsl(var(--surface-dark)))_50%,color-mix(in_srgb,hsl(var(--primary))_52%,hsl(var(--surface-dark)))_100%)]";

const TOGGLE_LABELS = {
  zoomV2: "Zoom V2",
  recordings: "Class Recordings",
  whatsapp: "WhatsApp Alerts",
  customDomain: "Custom Domain",
  aiMock: "AI Mock Interviews",
  aiLearning: "AI Learning",
  aiQuestionBank: "AI Question Bank",
  feeManagement: "Fee Management",
  enquiryManagement: "Enquiry Mgmt",
  interviews: "Interview Scheduling",
  codingPractice: "Coding Practice",
  assignments: "Assignments",
  customBranding: "Custom Branding",
  apiAccess: "API Access",
  enquiryFormBuilder: "Enquiry Form Builder",
};

const formatPrice = (amount) => {
  if (!amount || amount === 0) return "Free";
  return `₹${Number(amount).toLocaleString("en-IN")}`;
};

/**
 * UpgradeBanner — Smart dashboard banner that highlights what the
 * institute gains by upgrading to the next plan tier.
 *
 * Dismissable with 30-day re-appearance via localStorage.
 * Only renders if there's actually a higher plan available.
 */
const UpgradeBanner = () => {
  const navigate = useNavigate();
  const [bannerData, setBannerData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if dismissed recently (within last 30 days)
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const daysSince = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSince < 30) {
        setDismissed(true);
        return;
      }
    }

    const fetchBanner = async () => {
      try {
        const { data } = await apiClient.get("/billing/my-plan");
        if (data.upgradePlans && data.upgradePlans.length > 0) {
          // Pick the first (cheapest) upgrade — usually the "next tier"
          const nextPlan = data.upgradePlans[0];
          const currentPlan = data.currentPlan;

          // Calculate new features that would be unlocked
          const newFeatures = Object.keys(TOGGLE_LABELS).filter(
            (k) => nextPlan[k] && !currentPlan?.[k]
          );

          // Calculate limit upgrades
          const limitUpgrades = [];
          const limits = ["maxStudentsPerBatch", "maxBatches", "maxCourses", "maxInstructors"];
          limits.forEach((key) => {
            const cur = currentPlan?.[key] || 0;
            const next = nextPlan[key] || 0;
            if (next > cur) {
              const label = key === 'maxStudentsPerBatch' ? 'students per batch' : key.replace("max", "").toLowerCase();
              limitUpgrades.push(`${next} ${label}`);
            }
          });

          // Calculate usage pressure — are any resources at 80%+?
          const usage = data.usage || {};
          let hasUsagePressure = false;
          Object.entries(usage).forEach(([, v]) => {
            if (v.max > 0 && v.used / v.max >= 0.8) {
              hasUsagePressure = true;
            }
          });

          setBannerData({
            currentPlanName: currentPlan?.name || "Free",
            // Only free-plan institutes should see the upgrade banner.
            // A missing plan is treated as free.
            isFreePlan: currentPlan ? !!currentPlan.isFree : true,
            nextPlan,
            newFeatures,
            limitUpgrades,
            hasUsagePressure,
            subscription: data.subscription,
          });
        }
      } catch {
        // Banner is non-critical — fail silently
      }
    };

    fetchBanner();
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (dismissed || !bannerData) return null;

  // Show the upgrade banner only for institutes on the free plan.
  if (!bannerData.isFreePlan) return null;

  const { nextPlan, newFeatures, limitUpgrades, hasUsagePressure, subscription } = bannerData;
  const isTrial = subscription?.status === "trial";

  // Smart headline based on context
  let headline = "Unlock more for your institute";
  let subtext = `Upgrade to ${nextPlan.name} and take your operations to the next level.`;

  if (isTrial) {
    headline = "Your trial is active — get the full experience";
    subtext = `Subscribe to ${nextPlan.name} to keep your data and unlock premium features.`;
  } else if (hasUsagePressure) {
    headline = "You're running out of room";
    subtext = `Upgrade to ${nextPlan.name} for more capacity and premium features.`;
  }

  // Pick top 3 selling points
  const highlights = [];
  if (limitUpgrades.length > 0) {
    highlights.push(`Up to ${limitUpgrades[0]}`);
  }
  newFeatures.slice(0, 2).forEach((f) => {
    highlights.push(TOGGLE_LABELS[f]);
  });
  while (highlights.length < 3 && limitUpgrades.length > highlights.length) {
    highlights.push(`Up to ${limitUpgrades[highlights.length]}`);
  }

  return (
    <div
      className={`tw-relative ${PANEL_BG} tw-rounded-lg tw-px-6 tw-py-5 tw-overflow-hidden tw-text-on-surface-dark`}
    >
      <div className="tw-absolute -tw-top-[30px] -tw-right-[30px] tw-w-40 tw-h-40 tw-pointer-events-none tw-bg-[radial-gradient(circle,hsl(var(--primary)/0.35)_0%,transparent_70%)]" />
      {/* The breakpoint is the SCSS's own 768px — Tailwind's `md` is also 768px, so
          `max-md:` is exact here rather than merely close. */}
      <div className="tw-flex tw-items-center tw-gap-5 tw-relative tw-z-[1] max-md:tw-flex-col max-md:tw-items-start max-md:tw-gap-3.5">
        <div className="tw-w-11 tw-h-11 tw-rounded tw-bg-on-surface-dark/[0.15] tw-backdrop-blur-[8px] tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-text-[color-mix(in_srgb,hsl(var(--warning))_80%,white)] max-md:tw-hidden">
          <Sparkles size={20} />
        </div>
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-text-base tw-font-medium tw-tracking-[-0.01em] tw-leading-[1.3] tw-mb-0.5">
            {headline}
          </div>
          <div className="tw-text-[0.8125rem] tw-leading-[1.4] tw-text-on-surface-dark/[0.75]">
            {subtext}
          </div>
          {highlights.length > 0 && (
            <div className="tw-flex tw-flex-wrap tw-gap-2.5 tw-mt-2">
              {highlights.map((h, i) => (
                <span
                  key={i}
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-on-surface-dark/[0.9] tw-bg-on-surface-dark/[0.1] tw-px-2.5 tw-py-[0.2rem] tw-rounded-[6px] [&_svg]:tw-shrink-0 [&_svg]:tw-text-[color-mix(in_srgb,hsl(var(--success))_65%,white)]"
                >
                  <Check size={12} /> {h}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-shrink-0 max-md:tw-flex-row max-md:tw-w-full max-md:tw-justify-between">
          <div className="tw-flex tw-items-baseline tw-gap-1 tw-whitespace-nowrap">
            <span className="tw-text-[0.6875rem] tw-uppercase tw-tracking-[0.04em] tw-text-on-surface-dark/[0.5]">
              from
            </span>
            <span className="tw-text-xl tw-font-extrabold tw-tracking-[-0.02em]">
              {formatPrice(nextPlan.priceMonthly)}
            </span>
            <span className="tw-text-xs tw-text-on-surface-dark/[0.6]">/mo</span>
          </div>
          <button
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-5 tw-py-2 tw-text-[0.8125rem] tw-font-semibold tw-text-surface-dark tw-bg-on-surface-dark tw-border-0 tw-rounded-[10px] tw-cursor-pointer tw-whitespace-nowrap tw-transition-all hover:tw-bg-[color-mix(in_srgb,hsl(var(--on-surface-dark))_94%,hsl(var(--primary)))] hover:tw-shadow-[0_4px_12px_hsl(var(--surface-dark)/0.2)] hover:-tw-translate-y-px active:tw-translate-y-0"
            onClick={() => navigate("/billing")}
          >
            <Zap size={14} /> View Plans <ArrowRight size={14} />
          </button>
        </div>
      </div>
      <button
        className="tw-absolute tw-top-2.5 tw-right-2.5 tw-w-[26px] tw-h-[26px] tw-flex tw-items-center tw-justify-center tw-bg-on-surface-dark/[0.1] tw-border-0 tw-rounded-full tw-text-on-surface-dark/[0.5] tw-cursor-pointer tw-z-[2] tw-transition-all hover:tw-bg-on-surface-dark/[0.2] hover:tw-text-on-surface-dark"
        onClick={handleDismiss}
        title="Dismiss for 30 days"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default UpgradeBanner;
